import { useEventListener } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

import { useNordyUserStore } from '@/nordy/store/nordyUserStore'
import {
  getOrCreateAttributionContext,
  toMixpanelAttributionProperties
} from '@/nordy/utils/mixpanelAttribution'
import { mixpanelRuntime } from '@/nordy/utils/mixpanelRuntime'
import { syncMixpanelTrackingContext } from '@/nordy/utils/mixpanelTrackingContext'
import { registerMixpanelAnonymousUser } from '@/nordy/utils/mixpanelUserProperties'
import { trackFirstVisit } from '@/nordy/utils/userEngagementTracking'

interface MixpanelTrackingContextCoordinatorOptions {
  isAuthenticated: () => boolean
  initialize?: () => void
  sync: () => Promise<unknown>
}

export function createMixpanelTrackingContextCoordinator({
  isAuthenticated,
  initialize = () => {},
  sync
}: MixpanelTrackingContextCoordinatorOptions) {
  async function syncIfAuthenticated(): Promise<void> {
    if (!isAuthenticated()) return

    try {
      await sync()
    } catch {
      return
    }
  }

  return {
    async handleUserReady(): Promise<void> {
      try {
        initialize()
      } catch {
        // Analytics initialization must not block context synchronization.
      }
      await syncIfAuthenticated()
    },
    async handleVisibilityChange(
      visibilityState: DocumentVisibilityState
    ): Promise<void> {
      if (visibilityState !== 'visible') return
      await syncIfAuthenticated()
    }
  }
}

export function useMixpanelTrackingContextSync(): void {
  const userStore = useNordyUserStore()
  const { isUserLoaded, nordyUser } = storeToRefs(userStore)
  const coordinator = createMixpanelTrackingContextCoordinator({
    isAuthenticated: () => nordyUser.value !== null,
    initialize: () => {
      const context = getOrCreateAttributionContext({
        url: new URL(window.location.href),
        referrer: document.referrer
      })
      const attribution = toMixpanelAttributionProperties(context)
      if (!nordyUser.value) registerMixpanelAnonymousUser(attribution)
      mixpanelRuntime.register(attribution)
      mixpanelRuntime.registerOnce({
        first_source: context.sessionSource,
        first_medium: context.sessionMedium,
        first_campaign: context.rawUtm.utm_campaign,
        first_term: context.rawUtm.utm_term,
        first_content: context.rawUtm.utm_content,
        first_landing_page: context.location
      })
      trackFirstVisit(context)
    },
    sync: syncMixpanelTrackingContext
  })

  watch(
    isUserLoaded,
    (loaded) => {
      if (loaded) void coordinator.handleUserReady()
    },
    { immediate: true }
  )

  useEventListener(document, 'visibilitychange', () => {
    void coordinator.handleVisibilityChange(document.visibilityState)
  })
}
